import { getBackendBaseUrl } from "@/manager/backend-api-client/initializeBackendApiManager";
import { GLOBAL_API_CLIENT } from "@/modules/api/globalApiClient";
import { clientEnvModule } from "@/modules/client-env";

const HUDDLE_SESSION_ID_HEADER = "X-Mem-Huddle-Session-Id";
const HUDDLE_CLIENT_TIMEZONE_HEADER = "X-Mem-Client-Timezone";
const SDP_OFFER_REQUIRED_LINE_PREFIXES = [
  "v=0",
  "m=audio",
  "a=ice-ufrag:",
  "a=ice-pwd:",
  "a=fingerprint:",
  "a=setup:",
];

export type HuddleRealtimeConnectionStatus =
  | "requesting-microphone"
  | "connecting"
  | "connected"
  | "disconnected"
  | "ended";

export type HuddleTranscriptSpeaker = "user" | "assistant";

export type HuddleRealtimeTranscriptEvent = {
  speaker: HuddleTranscriptSpeaker;
  text: string;
  sourceEventType: string;
};

export type HuddleRealtimeToolCallEvent = {
  id: string;
  name: string;
  sourceEventType: string;
};

export type HuddleRealtimeSessionHandle = {
  huddleSessionId: string;
  setMicrophoneMuted: (isMuted: boolean) => void;
  stop: () => Promise<void>;
};

export type StartHuddleRealtimeSessionArgs = {
  onStatusChange?: (status: HuddleRealtimeConnectionStatus) => void;
  onTranscript?: (transcriptEvent: HuddleRealtimeTranscriptEvent) => void;
  onRealtimeEvent?: (realtimeEvent: unknown) => void;
};

type HuddleRealtimeCallResponse = {
  huddleSessionId: string;
  sdpAnswer: string;
};

type HuddleRealtimeResources = {
  audioElement: HTMLAudioElement | null;
  dataChannel: RTCDataChannel | null;
  localStream: MediaStream | null;
  peerConnection: RTCPeerConnection | null;
};

export const startHuddleRealtimeSession = async ({
  onStatusChange,
  onTranscript,
  onRealtimeEvent,
}: StartHuddleRealtimeSessionArgs = {}): Promise<HuddleRealtimeSessionHandle> => {
  if (!window.RTCPeerConnection) {
    throw new Error("Realtime voice is not supported in this browser.");
  }

  if (!navigator.mediaDevices?.getUserMedia) {
    throw new Error("Microphone capture is not supported in this browser.");
  }

  const resources: HuddleRealtimeResources = {
    audioElement: null,
    dataChannel: null,
    localStream: null,
    peerConnection: null,
  };
  let huddleSessionId: string | null = null;

  try {
    onStatusChange?.("requesting-microphone");
    const localStream = await navigator.mediaDevices.getUserMedia({ audio: true });

    onStatusChange?.("connecting");
    const peerConnection = new RTCPeerConnection();
    const audioElement = createAssistantAudioElement();
    const dataChannel = peerConnection.createDataChannel("oai-events");

    resources.localStream = localStream;
    resources.peerConnection = peerConnection;
    resources.audioElement = audioElement;
    resources.dataChannel = dataChannel;

    configurePeerConnection({
      resources,
      onStatusChange,
      onTranscript,
      onRealtimeEvent,
    });

    for (const track of localStream.getTracks()) {
      peerConnection.addTrack(track, localStream);
    }

    const offer = await peerConnection.createOffer();
    await peerConnection.setLocalDescription(offer);
    await waitForIceGatheringComplete(peerConnection);

    const sdpOffer = peerConnection.localDescription?.sdp;

    if (!sdpOffer) {
      throw new Error("Failed to create a WebRTC SDP offer.");
    }

    validateHuddleSdpOffer(sdpOffer);

    const realtimeCallResponse = await createHuddleRealtimeCall({ sdpOffer });
    huddleSessionId = realtimeCallResponse.huddleSessionId;
    const activeHuddleSessionId = realtimeCallResponse.huddleSessionId;

    await peerConnection.setRemoteDescription({
      type: "answer",
      sdp: realtimeCallResponse.sdpAnswer,
    });

    let hasStopped = false;

    return {
      huddleSessionId: activeHuddleSessionId,
      setMicrophoneMuted: isMuted => {
        setMediaStreamAudioTracksMuted({ localStream, isMuted });
      },
      stop: async () => {
        if (hasStopped) {
          return;
        }

        hasStopped = true;
        cleanupHuddleRealtimeResources(resources);
        onStatusChange?.("ended");

        await endHuddleRealtimeSession({ huddleSessionId: activeHuddleSessionId });
      },
    };
  } catch (error) {
    cleanupHuddleRealtimeResources(resources);

    if (huddleSessionId) {
      await endHuddleRealtimeSession({ huddleSessionId }).catch(() => undefined);
    }

    throw error;
  }
};

export const setMediaStreamAudioTracksMuted = ({
  localStream,
  isMuted,
}: {
  localStream: MediaStream | null;
  isMuted: boolean;
}) => {
  for (const track of localStream?.getAudioTracks() ?? []) {
    track.enabled = !isMuted;
  }
};

export const parseHuddleRealtimeTranscriptEvent = (
  realtimeEvent: unknown
): HuddleRealtimeTranscriptEvent | null => {
  if (!isRecord(realtimeEvent)) {
    return null;
  }

  const sourceEventType = getHuddleRealtimeEventType(realtimeEvent);
  const transcript = getStringProperty(realtimeEvent, "transcript")?.trim();

  if (sourceEventType === "conversation.item.input_audio_transcription.completed" && transcript) {
    return {
      speaker: "user",
      text: transcript,
      sourceEventType,
    };
  }

  if (isAssistantTranscriptDoneEvent(sourceEventType) && transcript) {
    return {
      speaker: "assistant",
      text: transcript,
      sourceEventType,
    };
  }

  const text = getStringProperty(realtimeEvent, "text")?.trim();

  if (sourceEventType === "response.output_text.done" && text) {
    return {
      speaker: "assistant",
      text,
      sourceEventType,
    };
  }

  return null;
};

export const parseHuddleRealtimeToolCallEvents = (
  realtimeEvent: unknown
): HuddleRealtimeToolCallEvent[] => {
  if (!isRecord(realtimeEvent)) {
    return [];
  }

  const sourceEventType = getHuddleRealtimeEventType(realtimeEvent);

  if (sourceEventType === "response.output_item.done") {
    const toolCallEvent = extractHuddleToolCallEventFromItem({
      item: realtimeEvent.item,
      sourceEventType,
    });

    return toolCallEvent ? [toolCallEvent] : [];
  }

  if (sourceEventType === "response.function_call_arguments.done") {
    const callId = getStringProperty(realtimeEvent, "call_id");
    const name =
      getStringProperty(realtimeEvent, "name") ??
      getHuddleToolCallNameFromItem({ item: realtimeEvent.item });

    if (!name) {
      return [];
    }

    return [
      {
        id: buildHuddleToolCallEventId({ callId, name, sourceEventType }),
        name,
        sourceEventType,
      },
    ];
  }

  if (sourceEventType === "response.done") {
    const response = realtimeEvent.response;

    if (!isRecord(response) || !Array.isArray(response.output)) {
      return [];
    }

    return response.output.flatMap(item => {
      const toolCallEvent = extractHuddleToolCallEventFromItem({
        item,
        sourceEventType,
      });

      return toolCallEvent ? [toolCallEvent] : [];
    });
  }

  return [];
};

export const getHuddleRealtimeEventType = (realtimeEvent: unknown): string | null => {
  if (!isRecord(realtimeEvent)) {
    return null;
  }

  return getStringProperty(realtimeEvent, "type");
};

export const getMissingHuddleSdpOfferMarkers = (sdpOffer: string): string[] => {
  const lines = getSdpOfferLines(sdpOffer);

  return SDP_OFFER_REQUIRED_LINE_PREFIXES.filter(requiredPrefix => {
    return !lines.some(line => line.startsWith(requiredPrefix));
  });
};

export const validateHuddleSdpOffer = (sdpOffer: string): void => {
  const missingMarkers = getMissingHuddleSdpOfferMarkers(sdpOffer);

  if (missingMarkers.length === 0) {
    return;
  }

  throw new Error(`Generated SDP offer is incomplete. Missing: ${missingMarkers.join(", ")}.`);
};

const configurePeerConnection = ({
  resources,
  onStatusChange,
  onTranscript,
  onRealtimeEvent,
}: {
  resources: HuddleRealtimeResources;
  onStatusChange?: (status: HuddleRealtimeConnectionStatus) => void;
  onTranscript?: (transcriptEvent: HuddleRealtimeTranscriptEvent) => void;
  onRealtimeEvent?: (realtimeEvent: unknown) => void;
}) => {
  const { peerConnection, dataChannel, audioElement } = resources;

  if (!peerConnection || !dataChannel || !audioElement) {
    throw new Error("Cannot configure Huddle without initialized WebRTC resources.");
  }

  peerConnection.addEventListener("connectionstatechange", () => {
    if (peerConnection.connectionState === "connected") {
      onStatusChange?.("connected");
      return;
    }

    if (
      peerConnection.connectionState === "disconnected" ||
      peerConnection.connectionState === "failed"
    ) {
      onStatusChange?.("disconnected");
    }
  });

  peerConnection.addEventListener("track", event => {
    audioElement.srcObject = event.streams[0] ?? null;
    void audioElement.play().catch(() => undefined);
  });

  dataChannel.addEventListener("message", event => {
    const realtimeEvent = parseRealtimeDataChannelMessage(event.data);

    if (!realtimeEvent) {
      return;
    }

    onRealtimeEvent?.(realtimeEvent);

    const transcriptEvent = parseHuddleRealtimeTranscriptEvent(realtimeEvent);

    if (transcriptEvent) {
      onTranscript?.(transcriptEvent);
    }
  });
};

const createAssistantAudioElement = () => {
  const audioElement = document.createElement("audio");
  audioElement.autoplay = true;

  return audioElement;
};

const waitForIceGatheringComplete = async (peerConnection: RTCPeerConnection): Promise<void> => {
  if (peerConnection.iceGatheringState === "complete") {
    return;
  }

  await new Promise<void>(resolve => {
    let hasResolved = false;
    let timeoutId: number | null = null;

    function cleanup() {
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }

      peerConnection.removeEventListener("icegatheringstatechange", handleIceGatheringStateChange);
    }

    function resolveOnce() {
      if (hasResolved) {
        return;
      }

      hasResolved = true;
      cleanup();
      resolve();
    }

    function handleIceGatheringStateChange() {
      if (peerConnection.iceGatheringState !== "complete") {
        return;
      }

      resolveOnce();
    }

    timeoutId = window.setTimeout(() => {
      resolveOnce();
    }, 1500);

    peerConnection.addEventListener("icegatheringstatechange", handleIceGatheringStateChange);
  });
};

const cleanupHuddleRealtimeResources = ({
  audioElement,
  dataChannel,
  localStream,
  peerConnection,
}: HuddleRealtimeResources) => {
  dataChannel?.close();
  peerConnection?.close();

  for (const track of localStream?.getTracks() ?? []) {
    track.stop();
  }

  if (audioElement) {
    audioElement.srcObject = null;
  }
};

const createHuddleRealtimeCall = async ({
  sdpOffer,
}: {
  sdpOffer: string;
}): Promise<HuddleRealtimeCallResponse> => {
  const response = await fetch(`${getBackendBaseUrl()}/v2/huddles/realtime/call`, {
    body: sdpOffer,
    credentials: "include",
    headers: await buildHuddleBackendHeaders({ contentType: "application/sdp" }),
    method: "POST",
  });

  if (!response.ok) {
    throw new Error(await buildBackendErrorMessage(response, "Failed to start Huddle."));
  }

  const huddleSessionId = response.headers.get(HUDDLE_SESSION_ID_HEADER);

  if (!huddleSessionId) {
    throw new Error("Backend did not return a Huddle session id.");
  }

  const sdpAnswer = await response.text();

  if (!sdpAnswer) {
    throw new Error("Backend did not return an SDP answer.");
  }

  return {
    huddleSessionId,
    sdpAnswer,
  };
};

const endHuddleRealtimeSession = async ({
  huddleSessionId,
}: {
  huddleSessionId: string;
}): Promise<void> => {
  const response = await fetch(`${getBackendBaseUrl()}/v2/huddles/${huddleSessionId}/end`, {
    credentials: "include",
    headers: await buildHuddleBackendHeaders(),
    method: "POST",
  });

  if (!response.ok) {
    throw new Error(await buildBackendErrorMessage(response, "Failed to end Huddle."));
  }
};

const buildHuddleBackendHeaders = async ({
  contentType,
}: {
  contentType?: string;
} = {}): Promise<Record<string, string>> => {
  const headers: Record<string, string> = {};
  const authToken = GLOBAL_API_CLIENT.getAuthToken();
  const packageVersion = clientEnvModule.packageVersion();
  const clientId = await clientEnvModule.clientId();
  const { clientGroupId } = await clientEnvModule.clientGroupId();

  if (contentType) {
    headers["Content-Type"] = contentType;
  }

  if (authToken) {
    headers["Authorization"] = `OAuthAccessToken ${authToken}`;
  }

  if (packageVersion) {
    headers["X-Mem-Client-Version"] = packageVersion;
    headers["X-Mem-Client-Platform"] = "web";
  }

  if (clientId) {
    headers["X-Mem-Client-ID"] = clientId;
  }

  if (clientGroupId) {
    headers["X-Mem-Client-Group-ID"] = clientGroupId;
  }

  const clientTimezone = getHuddleClientTimezone();

  if (clientTimezone) {
    headers[HUDDLE_CLIENT_TIMEZONE_HEADER] = clientTimezone;
  }

  return headers;
};

const getHuddleClientTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return null;
  }
};

const buildBackendErrorMessage = async (response: Response, fallbackMessage: string) => {
  const responseText = await response.text().catch(() => null);

  if (!responseText) {
    return `${fallbackMessage} (${response.status})`;
  }

  return `${fallbackMessage} (${response.status}): ${responseText}`;
};

const parseRealtimeDataChannelMessage = (dataChannelMessage: unknown): unknown | null => {
  if (typeof dataChannelMessage !== "string") {
    return null;
  }

  try {
    return JSON.parse(dataChannelMessage);
  } catch {
    return null;
  }
};

const getSdpOfferLines = (sdpOffer: string) => {
  return sdpOffer
    .split(/\r?\n/)
    .map(line => line.trim())
    .filter(line => line.length > 0);
};

const isAssistantTranscriptDoneEvent = (eventType: string | null) => {
  return (
    eventType === "response.audio_transcript.done" ||
    eventType === "response.output_audio_transcript.done"
  );
};

const extractHuddleToolCallEventFromItem = ({
  item,
  sourceEventType,
}: {
  item: unknown;
  sourceEventType: string | null;
}): HuddleRealtimeToolCallEvent | null => {
  if (!isRecord(item) || item.type !== "function_call") {
    return null;
  }

  const name = getStringProperty(item, "name");

  if (!name) {
    return null;
  }

  const callId = getStringProperty(item, "call_id");

  return {
    id: buildHuddleToolCallEventId({ callId, name, sourceEventType }),
    name,
    sourceEventType: sourceEventType ?? "unknown",
  };
};

const getHuddleToolCallNameFromItem = ({ item }: { item: unknown }) => {
  if (!isRecord(item)) {
    return null;
  }

  return getStringProperty(item, "name");
};

const buildHuddleToolCallEventId = ({
  callId,
  name,
  sourceEventType,
}: {
  callId: string | null;
  name: string;
  sourceEventType: string | null;
}) => {
  if (callId) {
    return callId;
  }

  return `${sourceEventType ?? "unknown"}:${name}`;
};

const getStringProperty = (value: Record<string, unknown>, key: string): string | null => {
  const propertyValue = value[key];

  if (typeof propertyValue !== "string") {
    return null;
  }

  return propertyValue;
};

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === "object" && value !== null && !Array.isArray(value);
};
